import util from './util'
import crypto from 'crypto-js'

const configs = {
  reconnectDelay: 1000,
  requestTimeout: 120 * 1000,
  retry: 3,
  broadcast: {
    channelName: 'ws_channel',
    lockKey: 'ws_leader_lock',
    lockTtl: 3000,
    heartBeatInterval: 1000,
    tabId: crypto.lib.WordArray.random(16).toString()
  }
}

class WebSocketClient {
  constructor() {
    this.wsClient = null
    this.broadcastChannel = null
    this.isSent = false
    this.isConnected = false
    this.eventHandlers = new Map()
    this.queues = {
      toSendWebSocket: new Set(),
      webSocket: new Map(),
      broadcastChannel: new Map()
    }
    setInterval(() => {
      this.runQueuedMessages()
    }, 1000)
    setInterval(() => {
      this.keepAlive()
    }, 30 * 1000)
    this.connectBroadcast()
  }

  uid() {
    return crypto.lib.WordArray.random(16).toString()
  }

  async accessToken() {
    return util.data.encode(`${util.options.user.id}|${util.cookie.get('XSRF-TOKEN', false)}`)
  }

  connectBroadcast() {
    this.broadcastChannel = new BroadcastChannel(configs.broadcast.channelName)
    this.broadcastChannel.onmessage = this.onMessageBroadcast.bind(this)
    setInterval(() => {
      this.tryBecomeLeader()
    }, configs.broadcast.heartBeatInterval)
  }

  tryBecomeLeader() {
    const lock = JSON.parse(localStorage.getItem(configs.broadcast.lockKey) || '{}')
    this.isBroadcastLeader = lock.tabId == configs.broadcast.tabId
    if (!this.isBroadcastLeader) {
      setTimeout(() => {
        const currentLock = JSON.parse(localStorage.getItem(configs.broadcast.lockKey) || '{}')
        this.isBroadcastLeader = currentLock.tabId == configs.broadcast.tabId
        if (!this.isBroadcastLeader) {
          this.disconnectWs()
        } else {
          this.connectWs()
        }
      }, 150)
    }
    if (this.isBroadcastLeader || !lock.expiresAt || lock.expiresAt < Date.now()) {
      localStorage.setItem(
        configs.broadcast.lockKey,
        JSON.stringify({
          tabId: configs.broadcast.tabId,
          expiresAt: Date.now() + configs.broadcast.lockTtl
        })
      )
    }
  }

  onMessageBroadcast(e) {
    if (e?.data?.type === 'ws-to-broadcast') {
      const { data, status } = e?.data || {}
      this.trigger(data.event, {
        status,
        data: data?.data
      })

      if (data?.level != 'private') return
      const object = this.queues.broadcastChannel.get(data.id)
      if (!object) return
      object?.[status != 'success' ? 'reject' : 'resolve'](data?.data)
      if (this.queues.webSocket.has(data.id)) this.queues.webSocket.delete(data.id)
      if (this.queues.broadcastChannel.has(data?.id)) {
        clearTimeout(object?.timeoutId)
        this.queues.broadcastChannel.delete(data?.id)
      }
    } else {
      const { message, force } = e?.data || {}
      this.queues.webSocket.set(message?.id, { message, retry: 0, timeoutId: null })
      this.queues.toSendWebSocket.add(message?.id)
      this.runQueuedMessage(message.id, force)
    }
  }

  connectWs() {
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
    const wsUrl = `${protocol}://${window.location.hostname || 'localhost'}/ws`
    this.wsClient = new WebSocket(wsUrl)
    this.wsClient.onopen = () => {
      this.trigger('connection', { status: 'connected' })
      this.sendMessage('register', null, true).then(() => {
        this.isConnected = true
        console.log('WebSocket connected')
      })
    }
    this.wsClient.onmessage = (event) => {
      let data, status, process
      try {
        if (event?.data == 'pong') return
        data = JSON.parse(event?.data)
        status = data?.status == 'success' ? 'success' : 'error'
        this.broadcastChannel.postMessage({ type: 'ws-to-broadcast', data, status })
        this.onMessageBroadcast({ data: { type: 'ws-to-broadcast', data, status } })
      } catch (error) {
        console.error('Error parsing message:', error)
      }
    }
    this.wsClient.onclose = (e) => {
      console.log('WebSocket disconnected')
      this.isConnected = false
      this.trigger('connection', { status: 'disconnected' })
      this.handleReconnectWs(e)
    }

    this.wsClient.onerror = (error) => {
      console.error('WebSocket error:', error)
      this.isConnected = false
      this.trigger('error', error)
    }
  }

  disconnectWs() {
    if (!this.wsClient) return
    this.wsClient.close()
    this.wsClient = null
  }

  handleReconnectWs(e) {
    if (!this.isBroadcastLeader && this.wsClient) this.wsClient = null
    if (!this.isBroadcastLeader || ![1005, 1006, 1011].includes(e.code)) return
    console.log(`Attempting to reconnect...`)
    setTimeout(() => this.connectWs(), configs.reconnectDelay)
  }

  async sendMessage(event, data, force) {
    const message = {
      id: this.uid(),
      level: 'private',
      event,
      data,
      timestamp: Date.now()
    }
    return new Promise((resolve, reject) => {
      this.queues.broadcastChannel.set(message.id, { message, resolve, reject, retry: 0, timeoutId: null })
      if (!this.isBroadcastLeader) this.broadcastChannel.postMessage({ type: 'broadcast-to-ws', message, force })
      else this.onMessageBroadcast({ data: { type: 'broadcast-to-ws', message, force } })
    })
  }

  async runQueuedMessage(id, force) {
    if (this.wsClient?.readyState !== WebSocket.OPEN || (!force && !this.isConnected)) return
    this.isSent = true
    const object = this.queues.webSocket.get(id)
    if (!object) return
    if (this.queues.toSendWebSocket.has(id)) this.queues.toSendWebSocket.delete(id)
    object.timeoutId = setTimeout(
      () => {
        object.retry++
        if (object.retry >= configs.retry) {
          object.reject(new Error('Timeout'))
          if (this.queues.webSocket.has(id)) this.queues.webSocket.delete(id)
          if (this.queues.broadcastChannel.has(id)) this.queues.broadcastChannel.delete(id)
        } else {
          this.queues.toSendWebSocket.add(id)
          this.runQueuedMessage(id, force)
        }
      },
      Math.ceil(configs.requestTimeout / configs.retry)
    )
    const message = Object.assign(
      {
        connectionId: configs.broadcast.tabId,
        token: await this.accessToken(),
        channel: 'web'
      },
      object.message
    )
    this.wsClient.send(JSON.stringify(message))
  }

  async runQueuedMessages() {
    if (this.wsClient?.readyState !== WebSocket.OPEN || !this.isConnected) return
    this.queues.toSendWebSocket.forEach((id) => {
      this.runQueuedMessage(id)
    })
  }

  async keepAlive() {
    if (!this.isSent && this.isConnected && this.wsClient?.send) this.wsClient.send('ping')
    this.isSent = false
  }

  on(event, handler) {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, new Set())
    }
    this.eventHandlers.get(event).add(handler)
  }

  off(event, handler) {
    if (this.eventHandlers.has(event)) {
      this.eventHandlers.get(event).delete(handler)
    }
  }

  trigger(event, data) {
    if (this.eventHandlers.has(event)) {
      this.eventHandlers.get(event).forEach((handler) => {
        try {
          handler(data)
        } catch (error) {
          console.error(`Error in event handler for ${event}:`, error)
        }
      })
    }
  }
}

// Export both the singleton instance and the class
export default WebSocketClient
